import * as yup from 'yup';

import { CountriesList } from '@/shared/constants/countries-list';
import { FormLabelItem, TDtoWrapper } from '@/shared/lib/forms/form-wrapper.types';
import { ContactDto, ContactType } from '@/shared/lib/sj-orm/models/contact.dto';
import { PrivateDocumentCategory } from '@/shared/lib/sj-orm/models/document/private.document.dto';
import { QuestionType } from '@/shared/lib/sj-orm/models/form/question.dto';

import {
  BankCellDetailsType,
  BankCellGeneralType,
} from '../../sj-orm/models/assets/categories/bankCell';
import { BankCellAccessMethod, BankCellType } from '../../sj-orm/models/assets/enums';
import { CollectionName } from '../../sj-orm/types';

import { assetsDocumentsBuilder, AssetsDocumentsPartType } from './builders/documents';
import { assetsOwnershipBuilder, AssetsOwnershipPartType } from './builders/ownership';
import { assetsProfitabilityBuilder, AssetsProfitabilityPartType } from './builders/profitability';
import { assetsStatisticsBuilder, AssetsStatisticsPartType } from './builders/statistics';
import { baseAssetFields } from './fields';

export type BankCell = BankCellGeneralType &
  BankCellDetailsType &
  Omit<AssetsOwnershipPartType, 'createdAt' | 'updatedAt'> &
  Omit<AssetsProfitabilityPartType, 'createdAt' | 'updatedAt'> &
  AssetsStatisticsPartType &
  Omit<AssetsDocumentsPartType, 'createdAt' | 'updatedAt'>;

export const BankCellList = [
  {
    key: BankCellType.standard,
    label: 'forms_assets:bankCells.enums.type.standard',
  },
  {
    key: BankCellType.safekeeping,
    label: 'forms_assets:bankCells.enums.type.safekeeping',
  },
];

export const BankCellAccessMethodsList = [
  {
    key: BankCellAccessMethod.keys,
    label: 'forms_assets:bankCells.enums.accessMethod.keys',
  },
  {
    key: BankCellAccessMethod.code,
    label: 'forms_assets:bankCells.enums.accessMethod.code',
  },
  {
    key: BankCellAccessMethod.fingerprint,
    label: 'forms_assets:bankCells.enums.accessMethod.fingerprint',
  },
  {
    key: BankCellAccessMethod.retina,
    label: 'forms_assets:bankCells.enums.accessMethod.retina',
  },
  {
    key: BankCellAccessMethod.other,
    label: 'forms_assets:bankCells.enums.accessMethod.other',
  },
];

type BankCellHelpGeneralType = {
  nickname: QuestionType.TEXT;
  country: QuestionType.SELECT_FROM_LIST;
  type: QuestionType.SELECT_FROM_LIST;
  purchaseValue: QuestionType.CURRENCY_CALCULATOR;
  isCashStorage: QuestionType.CHECKBOX;
  isOtherValuables: QuestionType.CHECKBOX;
  otherValuables: QuestionType.PICK_ARRAY_OF_DTO;
  bank: QuestionType.PICK_ARRAY_OF_DTO;
  documents: QuestionType.PICK_ARRAY_OF_DTO;
  isDocuments: QuestionType.CHECKBOX;
  accessMethod: QuestionType.SELECT_FROM_LIST;
  keyStorageLocation: QuestionType.TEXT;
};

type BankCellHelpDetailsType = {
  documents?: QuestionType.PICK_ARRAY_OF_DTO;
  depositBoxAddress?: QuestionType.TEXT;
  depositBoxNumber?: QuestionType.TEXT;
  depositBoxSize?: QuestionType.TEXT;
  openingDate?: QuestionType.DATE_TIME;
  validUntil?: QuestionType.DATE_TIME;
  specialAccessConditions?: QuestionType.CHECKBOX;
  specialAccessConditionsComment?: QuestionType.TEXT_MULTILINE;
  depositBoxAgreement?: QuestionType.PICK_ARRAY_OF_DTO;
  attachments?: QuestionType.FILE_UPLOAD;
};

type BankCellFormGeneralType = TDtoWrapper<BankCellGeneralType, BankCellHelpGeneralType>;
type BankCellFormDetailsType = TDtoWrapper<BankCellDetailsType, BankCellHelpDetailsType>;

const BankCellFormGeneral: BankCellFormGeneralType = {
  nickname: {
    type: QuestionType.TEXT,
    label: 'forms_assets:common.inputs.nickname.label',
    value: '',
    required: true,
    yupScheme: yup.string().required('forms_assets:common.inputs.nickname.errors.required'),
    colSpan: 2,
    order: 1,
  },
  country: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:common.inputs.country.label',
    tooltip: 'forms_assets:common.inputs.country.tooltip',
    value: '',
    required: true,
    items: CountriesList,
    withSearch: true,
    order: 2,
  },
  type: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:bankCells.inputs.type.label',
    value: BankCellType.standard,
    items: BankCellList,
    required: true,
    order: 3,
  },
  cashStorageLabel: {
    type: FormLabelItem.label,
    label: 'forms_assets:bankCells.inputs.cashStorageLabel.label',
    order: 7,
  },
  isCashStorage: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:bankCells.inputs.isCashStorage.label',
    tooltip: 'forms_assets:bankCells.inputs.isCashStorage.tooltip',
    order: 8,
  },
  purchaseValue: {
    type: QuestionType.CURRENCY_CALCULATOR,
    label: 'forms_assets:bankCells.inputs.amountInBaseCurrency.label',
    tooltip: 'forms_assets:bankCells.inputs.amountInBaseCurrency.tooltip',
    equivalentValueTooltip: 'forms_assets:common.inputs.equivalentValue.tooltip',
    value: 0,
    required: true,
    order: 4,
  },
  bank: {
    type: QuestionType.PICK_ONE_OF_DTOS,
    label: 'forms_assets:bankCells.inputs.bank.label',
    colName: CollectionName.CONTACTS,
    value: [],
    // required: true,
    yupScheme: yup.string().required('forms_assets:bankCells.inputs.bank.errors.required'),
    tooltip: 'forms_assets:bankCells.inputs.bank.tooltip',
    filter: (contact) =>
      [ContactType.BANK, ContactType.BROKER, ContactType.LEGAL_ENTITY].includes(
        (contact as unknown as ContactDto).type,
      ),
    dtoType: [ContactType.BANK, ContactType.BROKER, ContactType.LEGAL_ENTITY],
    colSpan: 2,
  },
  isDocuments: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:bankCells.inputs.isDocuments.label',
    order: 9,
  },
  documents: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:bankCells.inputs.documents.label',
    items: [CollectionName.PRIVATE_DOCUMENTS, CollectionName.PERSONAL_IDENTIFIERS_DOCUMENTS],
    mode: 'multiple',
    dtoType: PrivateDocumentCategory.BANK_DOCUMENTS,
    order: 12,
  },
  isOtherValuables: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:bankCells.inputs.isOtherValuables.label',
    order: 10,
    colSpan: 2,
  },
  otherValuables: {
    type: QuestionType.PICK_ARRAY_OF_DTO,
    label: 'forms_assets:bankCells.inputs.otherValuables.label',
    items: CollectionName.ASSETS,
    mode: 'multiple',
    order: 11,
  },
  accessMethod: {
    type: QuestionType.SELECT_FROM_LIST,
    label: 'forms_assets:bankCells.inputs.accessMethod.label',
    items: BankCellAccessMethodsList,
    value: BankCellAccessMethod.code,
    required: true,
    order: 5,
  },
  keyStorageLocation: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bankCells.inputs.keyStorageLocation.label',
    tooltip: 'forms_assets:bankCells.inputs.keyStorageLocation.tooltip',
    required: true,
    value: '',
    showIf: {
      accessMethod: {
        value: BankCellAccessMethod.keys,
      },
    },
    order: 6,
    colSpan: 2,
  },
};

const BankCellFormDetails: BankCellFormDetailsType = {
  depositBoxAddress: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bankCells.inputs.depositBoxAddress.label',
    order: 1,
  },
  depositBoxNumber: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bankCells.inputs.depositBoxNumber.label',
    order: 2,
  },
  depositBoxSize: {
    type: QuestionType.TEXT,
    label: 'forms_assets:bankCells.inputs.depositBoxSize.label',
    order: 3,
  },
  openingDate: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:bankCells.inputs.openingDate.label',
    order: 4,
  },
  validUntil: {
    type: QuestionType.DATE_TIME,
    label: 'forms_assets:bankCells.inputs.validUntil.label',
    order: 5,
    tooltip: 'forms_assets:bankCells.inputs.validUntil.tooltip',
  },
  specialAccessConditions: {
    type: QuestionType.CHECKBOX,
    label: 'forms_assets:bankCells.inputs.specialAccessConditions.label',
    order: 6,
  },
  specialAccessConditionsComment: {
    type: QuestionType.TEXT_MULTILINE,
    label: 'forms_assets:bankCells.inputs.specialAccessConditionsComment.label',
    showIf: {
      specialAccessConditions: {
        value: true,
      },
    },
    order: 7,
  },
  attachments: baseAssetFields.attachments,
};

const BankCellForm = [
  BankCellFormGeneral,
  BankCellFormDetails,
  assetsOwnershipBuilder().form,
  assetsProfitabilityBuilder([]),
  assetsStatisticsBuilder([
    'taxAgentWithBroker',
    'taxAgent',
    'publicFundTaxAgent',
    'taxAgentOther',
    'taxAgentWithBrokerOther',
    'publicTaxAgentOther',
    'legalTaxAgent',
    'legalTaxAgentOther',
    'holderTaxAgent',
    'holderTaxAgentOther',
    'specificsOfTaxation',
    'profitabilityFact',
    'profitabilityPlan',
  ]).form,
  assetsDocumentsBuilder(),
];

export { BankCellForm };
